import { cn } from '@/src/core/utils/cn'
import { useCallback, useEffect, useMemo } from 'react'
import { Button, FormField, Input } from '../../shared/ui'

export interface AmountPreset {
	label: string
	value: number
	shortcut?: string
}

export interface AmountInputProperties {
	value: number
	onChange: (value: number) => void
	currency?: string
	locale?: string
	min?: number
	max?: number
	increment?: number
	presets?: AmountPreset[]
	enableKeyboardShortcuts?: boolean
	error?: string
	label?: string
	hint?: string
	className?: string
	disabled?: boolean
}

const DEFAULT_PRESETS: AmountPreset[] = [
	{ label: '0 kr', value: 0, shortcut: '1' },
	{ label: '10 kr', value: 10, shortcut: '2' },
	{ label: '100 kr', value: 100, shortcut: '3' },
	{ label: '200 kr', value: 200, shortcut: '4' },
	{ label: '500 kr', value: 500, shortcut: '5' },
	{ label: '1000 kr', value: 1000, shortcut: '6' },
]

const KEYBOARD_SHORTCUTS = {
	UP: ['ArrowUp', 'k'],
	DOWN: ['ArrowDown', 'j'],
}

const DIGITS = '0123456789'

export function AmountInput({
	value,
	onChange,
	currency = 'kr',
	locale = 'no-NO',
	min = 0,
	max = 10_000,
	increment = 10,
	presets = DEFAULT_PRESETS,
	enableKeyboardShortcuts = true,
	error,
	label,
	hint,
	className,
	disabled = false,
}: AmountInputProperties) {
	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (disabled) return

			const rawValue = [...event.target.value].filter((char) => DIGITS.includes(char)).join('')
			const numericValue = Number.parseInt(rawValue || '0')

			// Apply min/max constraints
			const constrainedValue = Math.max(min, Math.min(max, numericValue))
			onChange(constrainedValue)
		},
		[onChange, min, max, disabled]
	)

	const formattedAmount = useMemo(() => {
		return new Intl.NumberFormat(locale).format(value)
	}, [value, locale])

	const handlePresetClick = useCallback(
		(amount: number) => {
			if (disabled) return
			onChange(amount)
		},
		[onChange, disabled]
	)

	const presetClickHandlers = useMemo(
		() => new Map(presets.map((preset) => [preset.value, () => handlePresetClick(preset.value)])),
		[presets, handlePresetClick]
	)

	const handleIncrement = useCallback(() => {
		if (disabled) return

		const newValue = value < 10 ? value + 9 : value + increment
		const constrainedValue = Math.min(max, newValue)
		onChange(constrainedValue)
	}, [value, increment, max, onChange, disabled])

	const handleDecrement = useCallback(() => {
		if (disabled) return

		const newValue = Math.max(min, value - increment)
		onChange(newValue)
	}, [value, increment, min, onChange, disabled])

	// Keyboard shortcuts
	useEffect(() => {
		if (!enableKeyboardShortcuts || disabled) return

		function handleKeyDown(event: KeyboardEvent) {
			// Ignore if focus is in an input
			if (event.target instanceof HTMLInputElement) {
				return
			}

			if (KEYBOARD_SHORTCUTS.UP.includes(event.key)) {
				event.preventDefault()
				handleIncrement()
			} else if (KEYBOARD_SHORTCUTS.DOWN.includes(event.key)) {
				event.preventDefault()
				handleDecrement()
			} else {
				// Handle preset shortcuts
				const preset = presets.find((p) => p.shortcut === event.key)
				if (preset) {
					event.preventDefault()
					handlePresetClick(preset.value)
				}
			}
		}

		globalThis.addEventListener('keydown', handleKeyDown)
		return () => globalThis.removeEventListener('keydown', handleKeyDown)
	}, [enableKeyboardShortcuts, handleIncrement, handleDecrement, handlePresetClick, presets, disabled])

	const isAtMin = value <= min
	const isAtMax = value >= max

	return (
		<FormField label={label} error={error} hint={hint} className={className}>
			<div className="space-y-6">
				<div className="relative flex items-center">
					<div className="absolute inset-y-0 left-0 flex items-stretch">
						<Button
							type="button"
							variant="increment"
							onClick={handleDecrement}
							disabled={disabled || isAtMin}
							className="z-10 flex h-full w-14 items-center justify-center rounded-l-md border-r border-white/10 bg-white/5 text-lg text-white/50 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#66d697] focus:ring-offset-2 focus:ring-offset-black disabled:cursor-not-allowed disabled:opacity-50"
							aria-label={`Decrease amount by ${increment} ${currency} (↓ or j)`}
						>
							-
						</Button>
					</div>
					<div className="relative flex-1">
						<div className="pointer-events-none absolute inset-y-0 left-14 flex items-center pl-6">
							<span className="text-3xl text-white/50">{currency}</span>
						</div>
						<Input
							variant="currency"
							type="text"
							inputMode="numeric"
							pattern="[0-9]*"
							value={formattedAmount}
							onChange={handleInputChange}
							disabled={disabled}
							error={!!error}
							aria-label={`Purchase amount in ${currency}`}
						/>
					</div>
					<div className="absolute inset-y-0 right-0 flex items-stretch">
						<Button
							type="button"
							variant="increment"
							onClick={handleIncrement}
							disabled={disabled || isAtMax}
							className="z-10 flex h-full w-14 items-center justify-center rounded-r-md border-l border-white/10 bg-white/5 text-lg text-white/50 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#66d697] focus:ring-offset-2 focus:ring-offset-black"
							aria-label={`Increase amount by ${increment} ${currency} (↑ or k)`}
						>
							+
						</Button>
					</div>
				</div>

				{/* Preset buttons */}
				{presets.length > 0 ? (
					<div className="flex flex-wrap gap-2">
						{presets.map((preset) => (
							<Button
								key={preset.value}
								type="button"
								variant="preset"
								onClick={presetClickHandlers.get(preset.value)}
								disabled={disabled}
								className={cn('flex-1', value === preset.value && 'ring-2 ring-[#66d697]')}
								aria-label={`Set amount to ${preset.label}${preset.shortcut ? ` (Press ${preset.shortcut})` : ''}`}
							>
								{preset.label}
							</Button>
						))}
					</div>
				) : undefined}

				{/* Keyboard shortcuts help */}
				{enableKeyboardShortcuts ? (
					<div className="sr-only" role="note">
						Keyboard shortcuts: Arrow keys or j/k to increment/decrement,{' '}
						{presets
							.map((p) => p.shortcut)
							.filter(Boolean)
							.join(', ')}{' '}
						for presets
					</div>
				) : undefined}
			</div>
		</FormField>
	)
}
